// ★ cmd-po_1135: 登録系モーダル po-form 共通部品 — catalog components.css v1.7 転写
// DOM契約: form-field / form-label / form-req / form-opt / form-note / form-helper /
//           form-split / form-check-group / form-check-row / form-file-drop
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Select, { SelectProps } from "@mui/material/Select";
import Checkbox, { CheckboxProps } from "@mui/material/Checkbox";
import FormHelperText from "@mui/material/FormHelperText";
import type { SxProps, Theme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UploadIcon from "@mui/icons-material/Upload";
import { useAtomValue } from "jotai";
import { AppAtoms } from "../../lib/store";

// ────────────────────────────────────────────────
// sx 定数（静的・isSmartPhone 非依存）
// ────────────────────────────────────────────────

// .po-field L74
const poFieldSx = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sp-05)",
} as const;

// .po-label L75
const poLabelSx = {
  fontSize: "var(--fs-12)",
  fontWeight: "var(--fw-semibold)",
  color: "var(--color-sumi)",
  display: "flex",
  alignItems: "center",
  gap: "var(--sp-2)",
} as const;

// .po-req L76
const poReqSx = {
  fontSize: "var(--fs-10)",
  fontWeight: "var(--fw-semibold)",
  color: "var(--color-kohaku-text)",
} as const;

// .fm-opt (ContractAddModal default_pc L17) — ※任意 / form-note 共用
const poOptSx = {
  fontSize: "var(--fs-10)",
  fontWeight: "var(--fw-semibold)",
  color: "var(--color-sumi-muted)",
} as const;

// .po-split L95
const poSplitSx = {
  display: "flex",
  gap: "var(--sp-2)",
  alignItems: "center",
  "& > *": { flex: 1 },
} as const;

// .po-helper L93
const buildPoHelperSx = (error?: boolean) => ({
  fontSize: "var(--fs-11)",
  color: error ? "var(--sem-error)" : "var(--color-sumi-muted)",
});

// .cbx L97-100
const cbxIconSx = {
  width: 16,
  height: 16,
  border: "1.5px solid var(--color-border)",
  borderRadius: "3px",
  backgroundColor: "var(--color-white)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
} as const;

// .cbx.on L101
const cbxCheckedSx = {
  ...cbxIconSx,
  backgroundColor: "var(--color-ai)",
  borderColor: "var(--color-ai)",
  color: "var(--color-white)",
} as const;

// .file-drop L109-112 (共通ベース)
const fileDropBaseSx = {
  borderRadius: "var(--radius-sm)",
  padding: "var(--sp-3)",
  display: "flex",
  alignItems: "center",
  gap: "var(--sp-2)",
  fontSize: "var(--fs-12)",
  cursor: "pointer",
  position: "relative",
} as const;

// idle
const fileDropSx = {
  ...fileDropBaseSx,
  border: "1.5px dashed var(--color-border)",
  color: "var(--color-sumi-muted)",
} as const;

// -attached (.file-drop.-attached)
const fileDropAttachedSx = {
  ...fileDropBaseSx,
  border: "1.5px solid var(--color-ai)",
  background: "var(--color-washi)",
  color: "var(--color-sumi)",
} as const;

// -error (.file-drop.-error)
const fileDropErrorSx = {
  ...fileDropBaseSx,
  border: "1.5px solid var(--sem-error)",
  background: "var(--sem-error-bg)",
  color: "var(--sem-error)",
} as const;

// fd-* 子要素 sx
const fdThumbSx = { width: 32, height: 32, borderRadius: "var(--radius-sm)", objectFit: "cover" as const, flexShrink: 0, background: "var(--color-border)" } as const;
const fdIconSx = { width: 32, height: 32, borderRadius: "var(--radius-sm)", background: "var(--color-washi-warm)", color: "var(--color-sumi-light)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 } as const;
const fdBodySx = { display: "flex", flexDirection: "column", gap: "var(--sp-05)", minWidth: 0, flex: 1 } as const;
const fdNameSx = { fontSize: "var(--fs-12)", color: "var(--color-sumi)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } as const;
const fdMetaSx = { fontSize: "var(--fs-11)", color: "var(--color-sumi-muted)" } as const;
const fdOkSx = { fontSize: "var(--fs-11)", fontWeight: "var(--fw-semibold)", color: "var(--sem-success)", display: "inline-flex", alignItems: "center", gap: "var(--sp-05)" } as const;
const fdDelSx = { marginLeft: "auto", width: 28, height: 28, flexShrink: 0, border: "none", background: "transparent", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "inherit", padding: 0 } as const;

// .fm-checks (ContractAddModal default_pc L18)
const checkGroupSx = {
  display: "flex",
  flexWrap: "wrap",
  rowGap: "var(--sp-2)",
  columnGap: "var(--sp-4)",
  padding: "var(--sp-1) 0",
} as const;

// .fm-check (ContractAddModal default_pc L19)
const checkRowSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--sp-2)",
  fontSize: "var(--fs-13)",
  color: "var(--color-sumi)",
} as const;

// ────────────────────────────────────────────────
// sx 定数（isSmartPhone 依存）
// ────────────────────────────────────────────────

// .po-input L77-82 / @media SP L472
function buildPoInputSx(isSmartPhone: boolean) {
  return {
    "& .MuiOutlinedInput-root": {
      height: isSmartPhone ? "var(--control-h-sp)" : "var(--control-h)",
      padding: "0 var(--sp-3)",
      border: "1px solid var(--color-border)",
      borderRadius: "var(--radius-sm)",
      fontFamily: "var(--font-sans)",
      fontSize: "var(--fs-13)",
      color: "var(--color-sumi)",
      backgroundColor: "var(--color-white)",
      display: "flex",
      alignItems: "center",
    },
    // .po-textarea L88-92 (multiline)
    "& .MuiInputBase-multiline.MuiOutlinedInput-root": {
      height: "auto",
      minHeight: "72px",
      padding: "var(--sp-2) var(--sp-3)",
      alignItems: "flex-start",
    },
    "& .MuiOutlinedInput-notchedOutline": { border: 0 },
    "& .MuiInputBase-input": { padding: 0, height: "auto" },
    // .ph L85
    "& .MuiInputBase-input::placeholder": {
      color: "var(--color-sumi-muted)",
      opacity: 1,
    },
    // .disabled L86
    "& .Mui-disabled": { backgroundColor: "var(--color-washi)" },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "var(--color-sumi-muted)",
    },
    // .-focus L83
    "& .MuiOutlinedInput-root.Mui-focused": {
      outline: "3px solid var(--state-focus-ring)",
      outlineOffset: 0,
      borderColor: "var(--color-ai-light)",
    },
    // .-error L87
    "& .MuiOutlinedInput-root.Mui-error": {
      borderColor: "var(--sem-error)",
    },
  };
}

// .po-input.sel L84 — PoSelect用
function buildPoSelectSx(isSmartPhone: boolean) {
  return {
    height: isSmartPhone ? "var(--control-h-sp)" : "var(--control-h)",
    padding: 0,
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-sm)",
    fontFamily: "var(--font-sans)",
    fontSize: "var(--fs-13)",
    color: "var(--color-sumi)",
    backgroundColor: "var(--color-white)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& .MuiOutlinedInput-notchedOutline": { border: 0 },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      padding: "0 var(--sp-3) !important",
      minHeight: "0 !important",
    },
    "& .MuiSvgIcon-root": { color: "var(--color-sumi-light)" },
  };
}

// ────────────────────────────────────────────────
// エクスポート部品
// ────────────────────────────────────────────────

/** PoField: .po-field — フォーム欄ルート (data-testid="form-field" or testId 上書き) */
export function PoField({
  fieldKey,
  testId,
  children,
  sx,
  'data-multi': dataMulti,
}: {
  fieldKey: string;
  /** data-testid 上書き（add-modal-field-<key>／edit-modal-field-<key> 等） */
  testId?: string;
  children: React.ReactNode;
  sx?: object;
  /** 複数選択欄マーカー（SerchSideBar MULTI_SELECT_KEYS 枝で "true" を渡す・recipeセレクタ用） */
  'data-multi'?: string;
}) {
  return (
    <Box
      data-testid={testId ?? "form-field"}
      data-field-key={fieldKey}
      data-multi={dataMulti}
      sx={{ ...poFieldSx, ...sx }}
    >
      {children}
    </Box>
  );
}

/** poAutocompleteSx (B-2 完成版・1142 rw2): MUI Autocomplete の外観を buildPoInputSx と揃える
 * `&&` = emotion specificity 倍化 → MUI 既定
 *   .MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot { padding-right: 65px }
 *   .MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot { padding-right: 39px }
 * に勝つために使用。効かなければ &&& へ昇格。
 * 値は buildPoInputSx の "& .MuiOutlinedInput-root" 宣言（L125-134）からコピー。
 * @media 639px は 1138 準拠（PoTextField は isSmartPhone(400) 判定=400-639 帯の既知乖離・alert_20260817_sp_breakpoint_unify 追跡）。
 */
export const poAutocompleteSx = {
  "&& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root": {
    height: "var(--control-h)",
    padding: "0 var(--sp-3)",
    paddingRight: "var(--sp-3)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-sm)",
    fontFamily: "var(--font-sans)",
    fontSize: "var(--fs-13)",
    color: "var(--color-sumi)",
    backgroundColor: "var(--color-white)",
    display: "flex",
    alignItems: "center",
    "@media (max-width:639px)": { height: "var(--control-h-sp)" },
  },
  /* MUI 既定 padding-right 65/39px を specificity で上書き */
  "&&.MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot, &&.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot": {
    paddingRight: "var(--sp-3)",
  },
  "&& .MuiOutlinedInput-notchedOutline": { border: 0 },
  "&& .MuiAutocomplete-input": { padding: 0, height: "auto" },
  "&& .MuiAutocomplete-endAdornment": { right: "var(--sp-2)" },
  "&& .MuiAutocomplete-inputRoot.Mui-focused": {
    outline: "3px solid var(--state-focus-ring)",
    outlineOffset: 0,
    borderColor: "var(--color-ai-light)",
  },
  "&& .MuiInputBase-input::placeholder": { color: "var(--color-sumi-muted)", opacity: 1 },
} as const;

/** PoLabel: .po-label — ラベル (data-testid="form-label" or testId 上書き)
 *  required/optional は排他。note は Contract 外部ET非表示 保全用
 *  testId: data-testid を上書きする（SerchSideBar 等で既存 "-label" testid を維持するため・1148追加） */
export function PoLabel({
  children,
  required,
  optional,
  note,
  htmlFor,
  testId,
}: {
  children: React.ReactNode;
  required?: boolean;
  optional?: boolean;
  note?: string;
  htmlFor?: string;
  testId?: string;
}) {
  return (
    <Box
      component="label"
      htmlFor={htmlFor}
      data-testid={testId ?? "form-label"}
      sx={poLabelSx}
    >
      {children}
      {required && (
        <Box component="span" data-testid="form-req" sx={poReqSx}>
          ※必須
        </Box>
      )}
      {optional && (
        <Box component="span" data-testid="form-opt" sx={poOptSx}>
          ※任意
        </Box>
      )}
      {note && (
        <Box component="span" data-testid="form-note" sx={poOptSx}>
          {note}
        </Box>
      )}
    </Box>
  );
}

/** PoTextField: .po-input — テキスト入力 (TextField wrapper) */
export function PoTextField(props: TextFieldProps) {
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const poInputSx = buildPoInputSx(isSmartPhone);
  return (
    <TextField
      size="small"
      variant="outlined"
      autoComplete="off"
      {...props}
      sx={{ ...poInputSx, ...props.sx }}
    />
  );
}

/** PoSelect: .po-input.sel — セレクト (Select wrapper) */
export function PoSelect(props: SelectProps) {
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const poSelectSx = buildPoSelectSx(isSmartPhone);
  return (
    <Select
      {...props}
      IconComponent={KeyboardArrowDownIcon}
      sx={{ ...poSelectSx, ...(props.sx as object) }}
    />
  );
}

/** PoCheckbox: .cbx — チェックボックス (MUI Checkbox wrapper) */
export function PoCheckbox({ sx, ...rest }: CheckboxProps) {
  return (
    <Checkbox
      {...rest}
      icon={<Box data-testid="form-cbx" sx={cbxIconSx} />}
      checkedIcon={
        <Box data-testid="form-cbx" data-on="true" sx={cbxCheckedSx}>
          <CheckIcon sx={{ fontSize: 11 }} />
        </Box>
      }
      disableRipple
      sx={{ p: 0.5, ...(sx as object) }}
    />
  );
}

/** PoHelper: .po-helper — ヘルパーテキスト (data-testid="form-helper") */
export function PoHelper({
  children,
  error,
  sx,
}: {
  children: React.ReactNode;
  error?: boolean;
  sx?: SxProps<Theme>;
}) {
  return (
    <FormHelperText
      data-testid="form-helper"
      error={!!error}
      sx={Object.assign({}, buildPoHelperSx(error), sx ?? {}) as SxProps<Theme>}
    >
      {children}
    </FormHelperText>
  );
}

/** PoSplit: .po-split — 横並び分割欄 (data-testid="form-split") */
export function PoSplit({ children }: { children: React.ReactNode }) {
  return (
    <Box data-testid="form-split" sx={poSplitSx}>
      {children}
    </Box>
  );
}

/** PoCheckGroup: .fm-checks — チェックボックス群コンテナ (data-testid="form-check-group") */
export function PoCheckGroup({ children }: { children: React.ReactNode }) {
  return (
    <Box data-testid="form-check-group" sx={checkGroupSx}>
      {children}
    </Box>
  );
}

/** PoCheckRow: .fm-check — チェックボックス行 (data-testid="form-check-row") */
export function PoCheckRow({ children }: { children: React.ReactNode }) {
  return (
    <Box component="label" data-testid="form-check-row" sx={checkRowSx}>
      {children}
    </Box>
  );
}

// ────────────────────────────────────────────────
// バリデーション ユーティリティ（cmd-po_1144）
// ────────────────────────────────────────────────

/** フォームエラーメッセージ定数 */
export const PO_FORM_MSG = {
  format: '形式が正しくありません',
} as const;

/** メールアドレス形式バリデーション */
export const isValidMail = (v: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

/** 電話番号形式バリデーション（tel_flg / form_tel_flg 共通）
 *  /^[0-9-]{10,13}$/ かつ数字部分 10-11 桁 */
export const isValidTel = (v: string) => {
  const digits = v.replace(/-/g, '');
  return /^[0-9-]{10,13}$/.test(v) && digits.length >= 10 && digits.length <= 11;
};

/** 郵便番号形式バリデーション（/^\d{7}$/ または /^\d{3}-?\d{4}$/） */
export const isValidZip = (v: string) =>
  /^\d{7}$/.test(v) || /^\d{3}-?\d{4}$/.test(v);

/** PoFileDrop: .file-drop — ファイル選択 (data-testid="form-file-drop")
 *  input[type=file] は DOM に残す（E2E setInputFiles 経路維持）
 *  idle / -attached / -error の3描画分岐 */
export function PoFileDrop({
  inputProps,
  label = "クリックしてファイルを選択",
  error,
  onClear,
}: {
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  label?: string;
  /** 失敗文言。渡すと -error variant を描画 */
  error?: string;
  /** × 削除クリック時に呼出側の File state をクリアするコールバック */
  onClear?: () => void;
}) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewURL, setPreviewURL] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewURL) URL.revokeObjectURL(previewURL);
    };
  }, [previewURL]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (previewURL) URL.revokeObjectURL(previewURL);
    setSelectedFile(file);
    setPreviewURL(file?.type.startsWith("image/") ? URL.createObjectURL(file) : null);
    inputProps?.onChange?.(e);
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (previewURL) URL.revokeObjectURL(previewURL);
    setSelectedFile(null);
    setPreviewURL(null);
    onClear?.();
  };

  const hiddenInput = (
    <input
      type="file"
      {...inputProps}
      onChange={handleChange}
      style={{ position: "absolute", width: 1, height: 1, opacity: 0 }}
    />
  );

  const delBtn = (
    <Box
      component="button"
      type="button"
      aria-label="削除"
      sx={fdDelSx}
      onClick={handleClear}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M18 6 6 18M6 6l12 12"/>
      </svg>
    </Box>
  );

  const pdfIcon = (
    <Box component="span" sx={fdIconSx}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9z"/>
        <path d="M13 3v6h6"/>
      </svg>
    </Box>
  );

  // -error variant
  if (error) {
    return (
      <Box component="label" data-testid="form-file-drop" sx={fileDropErrorSx}>
        {pdfIcon}
        <Box sx={fdBodySx}>
          {selectedFile && <Box component="span" sx={fdNameSx}>{selectedFile.name}</Box>}
          <Box component="span" sx={{ ...fdMetaSx, color: "var(--sem-error)" }}>{error}</Box>
        </Box>
        {delBtn}
        {hiddenInput}
      </Box>
    );
  }

  // -attached variant
  if (selectedFile) {
    const sizeKB = Math.max(1, Math.round(selectedFile.size / 1024));
    const sizeLabel = sizeKB >= 1024 ? `${(sizeKB / 1024).toFixed(1)} MB` : `${sizeKB} KB`;
    const isImage = selectedFile.type.startsWith("image/");
    return (
      <Box component="label" data-testid="form-file-drop" sx={fileDropAttachedSx}>
        {isImage && previewURL ? (
          <Box component="img" src={previewURL} alt="" sx={fdThumbSx} />
        ) : pdfIcon}
        <Box sx={fdBodySx}>
          <Box component="span" sx={fdNameSx}>{selectedFile.name}</Box>
          <Box component="span" sx={fdMetaSx}>{sizeLabel}</Box>
        </Box>
        <Box component="span" sx={fdOkSx}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="m5 13 4 4L19 7"/>
          </svg>
          添付済み
        </Box>
        {delBtn}
        {hiddenInput}
      </Box>
    );
  }

  // idle
  return (
    <Box component="label" data-testid="form-file-drop" sx={fileDropSx}>
      <UploadIcon sx={{ fontSize: 18 }} />
      {label}
      {hiddenInput}
    </Box>
  );
}
